import {
  GoogleAdsAdGroup,
  GoogleAdsCampaignRow,
  GoogleAdsCampaignStatus,
} from "../api/google-ads";
import {
  KeywordAgent,
  KeywordAgentPauseReason,
  KeywordAgentStatus,
} from "../api/keyword-agent";

export type KeywordAgentDisplayStatus = "active" | "learning" | "inactive";

// Structural rather than a Pick of the ad group, so the agent page — which
// holds the criteria itself rather than a row's copy of the flag —
// satisfies it too.
export const getKeywordAgentDisplayStatus = (row: {
  agent: KeywordAgent | null;
  "criteria-reviewed?": boolean;
}): KeywordAgentDisplayStatus => {
  const { agent } = row;
  if (!agent || agent.status !== KeywordAgentStatus.ACTIVE) {
    return "inactive";
  }
  return row["criteria-reviewed?"] ? "active" : "learning";
};

export const KEYWORD_AGENT_STATUS_LABELS: Record<
  KeywordAgentDisplayStatus,
  string
> = {
  active: "Active",
  learning: "Learning",
  inactive: "Inactive",
};

export const KEYWORD_AGENT_STATUS_TONES: Record<
  KeywordAgentDisplayStatus,
  "green" | "yellow" | "gray"
> = {
  active: "green",
  learning: "yellow",
  inactive: "gray",
};

export const formatPauseReason = (
  reason: KeywordAgentPauseReason | null,
): string | null => {
  switch (reason) {
    case KeywordAgentPauseReason.AD_GROUP_DELETED:
      return "The ad group was removed in Google Ads.";
    case KeywordAgentPauseReason.CAMPAIGN_DELETED:
      return "The campaign was removed in Google Ads.";
    case KeywordAgentPauseReason.INTEGRATION_DISCONNECTED:
      return "Google Ads was disconnected. Reconnect it to start this agent again.";
    case KeywordAgentPauseReason.PAUSED_BY_USER:
      return null;
    default:
      return null;
  }
};

// Google never reuses an id, so an agent stopped because the thing it
// covered is gone can never be started again.
export const isResumable = (reason: KeywordAgentPauseReason | null): boolean =>
  reason !== KeywordAgentPauseReason.AD_GROUP_DELETED &&
  reason !== KeywordAgentPauseReason.CAMPAIGN_DELETED;

// Only Search campaigns have search terms. The hub still lists the rest,
// but doesn't link through to them.
export const isSupportedGoogleCampaign = (
  campaign: Pick<GoogleAdsCampaignRow, "advertising-channel-type">,
): boolean => campaign["advertising-channel-type"] === "SEARCH";

// A campaign and an ad group carry the same status enum.
export const isPausedInGoogle = (row: {
  status: GoogleAdsCampaignStatus;
}): boolean => row.status === GoogleAdsCampaignStatus.PAUSED;

export type CampaignTab = "all" | "active" | "needs-review";

export const matchesCampaignTab = (
  campaign: Pick<GoogleAdsCampaignRow, "ad-groups" | "stats">,
  tab: CampaignTab,
): boolean => {
  switch (tab) {
    case "active":
      return campaign["ad-groups"].active > 0;
    case "needs-review":
      return campaign.stats["pending-review"] > 0;
    default:
      return true;
  }
};

export const matchesAdGroupTab = (
  adGroup: Pick<GoogleAdsAdGroup, "agent" | "criteria-reviewed?" | "stats">,
  tab: CampaignTab,
): boolean => {
  switch (tab) {
    case "active":
      return getKeywordAgentDisplayStatus(adGroup) !== "inactive";
    case "needs-review":
      return adGroup.stats["pending-review"] > 0;
    default:
      return true;
  }
};
